import { schema, toHTML } from 'ngx-editor';

/** A node from an ngx-editor / ProseMirror document. */
interface RichTextNode {
  type?: string;
  text?: string;
  attrs?: Record<string, any>;
  marks?: Array<{ type?: string; attrs?: Record<string, any> }>;
  content?: RichTextNode[];
}

/**
 * Task descriptions are rich text (ngx-editor) and the API contract is a plain
 * string holding HTML. This maps anything we can be handed back onto that
 * contract, so no document object ever reaches the API:
 *
 *  - HTML string            → returned as-is
 *  - empty / blank          → null
 *  - ProseMirror doc object → serialised to HTML
 *  - JSON-stringified doc   → parsed, then serialised to HTML
 *
 * The last two cases cover records written before the description field became
 * a rich text box and a brief regression that stored the editor's document
 * object instead of a string.
 */
export function richTextToHtml(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'object') {
    return fromDoc(value as RichTextNode);
  }

  const raw = String(value).trim();
  if (!raw) {
    return null;
  }

  if (looksLikeDocJson(raw)) {
    try {
      return fromDoc(JSON.parse(raw) as RichTextNode);
    } catch {
      // Not valid JSON after all — fall through and treat it as plain text.
    }
  }

  return raw;
}

function looksLikeDocJson(raw: string): boolean {
  return raw.startsWith('{') && raw.includes('"type"') && raw.includes('"content"');
}

function fromDoc(doc: RichTextNode): string | null {
  const text = docToText(doc).replace(/\u00a0/g, ' ').trim();
  if (!text) {
    return null;
  }
  try {
    const html = toHTML(doc as unknown as Record<string, unknown>, schema);
    if (html && html.trim()) {
      return html;
    }
  } catch {
    // Doc doesn't match the current schema — degrade to readable plain text.
  }
  return docToPlainText(doc);
}

/** Block-aware plain-text projection, so an empty doc reads as empty. */
function docToText(node: RichTextNode): string {
  if (node.type === 'text') {
    return node.text ?? '';
  }
  if (node.type === 'hard_break') {
    return '\n';
  }
  if (node.type === 'image') {
    return node.attrs?.alt || node.attrs?.title || '';
  }

  const inner = (node.content ?? []).map(docToText).join('');
  switch (node.type) {
    case 'paragraph':
    case 'heading':
    case 'blockquote':
    case 'code_block':
    case 'list_item':
      return `${inner}\n`;
    default:
      return inner;
  }
}

/** Last-resort projection that keeps bold/italic/code readable as markup. */
function docToPlainText(doc: RichTextNode): string {
  const walk = (node: RichTextNode): string => {
    if (node.type === 'text') {
      const marks = new Set((node.marks ?? []).map(m => m?.type));
      const text = node.text ?? '';
      if (marks.has('strong') || marks.has('b')) return `**${text}**`;
      if (marks.has('em') || marks.has('i')) return `*${text}*`;
      if (marks.has('code')) return `\`${text}\``;
      return text;
    }
    if (node.type === 'hard_break') {
      return '\n';
    }
    return (node.content ?? []).map(walk).join('');
  };

  return walk(doc).replace(/\u00a0/g, ' ').trim();
}
